import { Injectable, Inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import {CookieService} from 'ngx-cookie-service';

export type ThemeMode = 'light' | 'dark' | 'auto';

@Injectable({
  providedIn: 'root'
})
export class SwitchThemeService implements OnDestroy {
  private readonly storageKey = 'theme-preference';
  private readonly themeAttribute = 'data-theme';

  public currentTheme$ = new BehaviorSubject<ThemeMode>(this.cookieService.get(this.storageKey) as ThemeMode || 'auto');
  private isDarkMode$ = new BehaviorSubject<boolean>(false);
  
  private isBrowser: boolean;
  private mediaQueryList: MediaQueryList | null = null;
  private mediaQueryHandler = (e: MediaQueryListEvent) => {
    if (this.currentTheme$.value === 'auto') {
      this.applyTheme('auto');
    }
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    public cookieService: CookieService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      this.mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
    }
    this.initializeTheme();
    this.setupMediaQueryListener();
  }

  ngOnDestroy(): void {
    if (this.mediaQueryList) {
      this.mediaQueryList.removeEventListener('change', this.mediaQueryHandler);
    }
  }

  /*
   * Get the current theme mode observable
   */
  getCurrentTheme(): Observable<ThemeMode> {
    return this.currentTheme$.asObservable();
  }

  /*Get the dark mode state observable*/
  isDarkMode(): Observable<boolean> {
    return this.isDarkMode$.asObservable();
  }

  setTheme(theme: ThemeMode) {
    this.applyTheme(theme);
    this.currentTheme$.next(theme);
    this.saveThemePreference(theme);
  }

  /*Toggle between light and dark modes*/
  toggleTheme() {
    const currentTheme = this.currentTheme$.value;
    const newTheme: ThemeMode = currentTheme === 'dark' ? 'light' : 'dark';
    this.setTheme(newTheme);
  }

  /*Get current theme mode value (synchronous)*/
  getCurrentThemeValue(): ThemeMode {
    return this.currentTheme$.value;
  }

  /* Get current dark mode state (synchronous)*/
  isDarkModeValue(): boolean {
    return this.isDarkMode$.value;
  }

  /*Initialize theme from localStorage or system preference*/
  initializeTheme() {
    const savedTheme = this.getSavedThemePreference();
    const initialTheme: ThemeMode = savedTheme || 'auto';
    
    this.currentTheme$.next(initialTheme);
    this.applyTheme(initialTheme);
  }

  /* Apply the theme to the document*/
   applyTheme(theme: ThemeMode): void {
    const isDark = this.calculateDarkMode(theme);

    const documentElement = this.document.documentElement;

    // setAttribute FIRST so CSS vars are live before subscribers read them
    documentElement.removeAttribute(this.themeAttribute);
    const effectiveTheme = (theme === 'dark' || (theme === 'auto' && isDark)) ? 'dark' : 'light';
    documentElement.setAttribute(this.themeAttribute, effectiveTheme);

    // THEN emit — BehaviorSubject fires synchronously, so CSS vars are already applied
    this.isDarkMode$.next(isDark);
  }

  /* Calculate if dark mode should be active */
  calculateDarkMode(theme: ThemeMode): boolean {
    switch (theme) {
      case 'dark':
        return true;
      case 'light':
        return false;
      case 'auto':
        return this.mediaQueryList?.matches ?? false;
      default:
        return false;
    }
  }

  /* Setup media query listener for auto mode */
  setupMediaQueryListener(): void {
    if (this.mediaQueryList) {
      this.mediaQueryList.addEventListener('change', this.mediaQueryHandler);
    }
  }

  /* Save theme preference to localStorage */
  saveThemePreference(theme: ThemeMode): void {
    try {
      localStorage.setItem(this.storageKey, theme);
      this.cookieService.set(this.storageKey, theme, { path: '/' });
    } catch (error) {
      console.error('Could not save theme preference to localStorage:', error);
    }
  }

  /* 
   * Get saved theme preference from localStorage, falling back to cookie
   * Returns the saved theme if valid, 'auto' as fallback, or null on error
   */
  getSavedThemePreference(): ThemeMode | null {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (saved === 'light' || saved === 'dark' || saved === 'auto') {
        return saved;
      }
      // localStorage was empty/invalid — fall back to cookie
      const fromCookie = this.cookieService.get(this.storageKey);
      if (fromCookie === 'light' || fromCookie === 'dark' || fromCookie === 'auto') {
        // Re-sync localStorage so they stay in agreement
        localStorage.setItem(this.storageKey, fromCookie);
        return fromCookie;
      }
      return 'auto';
    } catch (error) {
      console.error('Could not read theme preference from localStorage:', error);
      return null;
    }
  }

  /* Get theme icon for UI display */
  getThemeIcon(theme?: ThemeMode): string {
    const currentTheme = theme || this.currentTheme$.value;
    switch (currentTheme) {
      case 'light':
        return 'pi pi-sun';
      case 'dark':
        return 'pi pi-moon';
      case 'auto':
        return 'pi pi-desktop';
      default:
        return 'pi pi-desktop';
    }
  }

  /* Get theme display name for UI */
  getThemeDisplayName(theme?: ThemeMode): string {
    const currentTheme = theme || this.currentTheme$.value;
    switch (currentTheme) {
      case 'light':
        return 'Light';
      case 'dark':
        return 'Dark';
      case 'auto':
        return 'Auto';
      default:
        return 'Auto';
    }
  }
}